import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Search } from "lucide-react";
import { paths } from "@shared-core/routing/routes";
import {
  PURCHASE_ORDER_LIST_TABS,
  type PurchaseOrderListItem,
  type PurchaseOrderListTab,
} from "@shared-core/data/supplier-portal-adapter";
import { useSupplierPortalAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { useLoad } from "@shared-core/domain/useLoad";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  ACTION_WORDING,
  LIST_WORDING,
  dayMonth,
  listItemStatus,
  listItemWhat,
  purchaseOrderTabLabel,
  tabCountTone,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { Input } from "../../app/components/primitives/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { cn } from "../../app/components/primitives/utils";
import { StatusPill } from "../../app/components/domain/StatusPill";
import { CountBadge } from "../shell/SignedInShell";
import {
  EmptyState,
  LoadFailed,
  LoadingCard,
  SectionHeading,
} from "../shell/states";

const LOGIN = { via: "login" } as const;
const PAGE_SIZE = 25;

function tabFrom(value: string | null): PurchaseOrderListTab {
  return PURCHASE_ORDER_LIST_TABS.find((t) => t === value) ?? "to_accept";
}

/**
 * `/orders` (login only): every purchase order addressed to the supplier, by tab (To accept / In production /
 * Despatched / Closed) with what is waiting in each, searchable. Filtering, search and paging are the server's
 * (docs/list-endpoints.md); the page only renders. On To accept, the orders in production that are overdue sit
 * underneath, so nothing late hides on another tab.
 */
export function OrdersPage() {
  const adapter = useSupplierPortalAdapter();
  const [params, setParams] = useSearchParams();
  const tab = tabFrom(params.get("tab"));
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [query, setQuery] = useState(params.get("q") ?? "");
  const q = useDebouncedValue(query.trim(), 300);

  // A new search starts again at page 1 (list-endpoints contract).
  useEffect(() => {
    if ((params.get("q") ?? "") === q) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (q) next.set("q", q);
        else next.delete("q");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [q, params, setParams]);

  const list = useLoad(
    () =>
      adapter.listPurchaseOrders(LOGIN, {
        tab,
        q: q || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    `${tab}|${q}|${page}`,
  );
  const attention = useLoad(
    () =>
      tab === "to_accept"
        ? adapter.listPurchaseOrders(LOGIN, {
            tab: "in_production",
            overdueOnly: true,
            q: q || undefined,
          })
        : Promise.resolve(null),
    `attention|${tab}|${q}`,
  );

  const tabHref = (next: PurchaseOrderListTab) => {
    const search = new URLSearchParams();
    if (next !== "to_accept") search.set("tab", next);
    if (q) search.set("q", q);
    const text = search.toString();
    return text ? `?${text}` : paths.supplierOrders();
  };
  const pageHref = (n: number) => {
    const search = new URLSearchParams(params);
    if (n > 1) search.set("page", String(n));
    else search.delete("page");
    return `?${search.toString()}`;
  };

  return (
    <div className="grid gap-4">
      <h1 className="sr-only">Orders</h1>
      <div className="flex flex-wrap items-end gap-x-1 gap-y-3">
        <nav aria-label="Order status" className="flex gap-1 overflow-x-auto">
          {PURCHASE_ORDER_LIST_TABS.map((t) => {
            const n = list.data?.tabCounts[t];
            const tone = tabCountTone(t);
            return (
              <Link
                key={t}
                to={tabHref(t)}
                aria-current={t === tab ? "page" : undefined}
                className={cn(
                  "border-b-2 px-2.5 py-2 text-sm whitespace-nowrap",
                  t === tab
                    ? "border-primary font-semibold text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {purchaseOrderTabLabel(t)}
                {tone && n !== undefined && n > 0 && (
                  <CountBadge n={n} tone={tone} label={`${n} orders`} />
                )}
              </Link>
            );
          })}
        </nav>
        <span className="flex-1" />
        <label className="relative w-full sm:w-64">
          <span className="sr-only">{LIST_WORDING.searchPlaceholder}</span>
          <Search
            aria-hidden
            className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-muted-foreground"
          />
          <Input
            type="search"
            className="pl-8"
            placeholder={LIST_WORDING.searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      </div>

      {list.data ? (
        list.data.items.length === 0 ? (
          <Card>
            <EmptyState
              title={q ? LIST_WORDING.noMatch(q) : LIST_WORDING.empty(tab)}
            />
          </Card>
        ) : (
          <>
            <OrdersTable items={list.data.items} withHeader />
            {list.data.totalPages > 1 && (
              <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
                Page {list.data.page} of {list.data.totalPages}
                {page > 1 && (
                  <Button asChild variant="outline" size="sm">
                    <Link to={pageHref(page - 1)}>Previous</Link>
                  </Button>
                )}
                {page < list.data.totalPages && (
                  <Button asChild variant="outline" size="sm">
                    <Link to={pageHref(page + 1)}>Next</Link>
                  </Button>
                )}
              </div>
            )}
          </>
        )
      ) : list.error ? (
        <LoadFailed
          what="your orders"
          message={mutationErrorMessage(
            list.error,
            "Something went wrong reaching Pollards.",
          )}
          onRetry={list.reload}
        />
      ) : (
        <LoadingCard what="your orders" />
      )}

      {attention.data && attention.data.items.length > 0 && (
        <>
          <SectionHeading>{LIST_WORDING.needingAttention}</SectionHeading>
          <OrdersTable items={attention.data.items} withHeader={false} />
        </>
      )}
    </div>
  );
}

function OrdersTable({
  items,
  withHeader,
}: {
  items: PurchaseOrderListItem[];
  withHeader: boolean;
}) {
  return (
    <Card className="gap-0 overflow-x-auto py-0">
      <Table>
        {withHeader && (
          <TableHeader>
            <TableRow>
              <TableHead>Purchase order</TableHead>
              <TableHead>What</TableHead>
              <TableHead>Ready by</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>
                <span className="sr-only">Open</span>
              </TableHead>
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          {items.map((item) => {
            const status = listItemStatus(item);
            return (
              <TableRow key={item.purchase_order_no}>
                <TableCell className="font-mono text-xs">
                  {item.purchase_order_no}
                </TableCell>
                <TableCell className="whitespace-normal">
                  {listItemWhat(item)}
                </TableCell>
                <TableCell>
                  {item.ready_by ? dayMonth(item.ready_by) : "—"}
                </TableCell>
                <TableCell>
                  <StatusPill label={status.label} tone={status.tone} />
                </TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="outline" size="sm">
                    <Link
                      to={paths.supplierPurchaseOrder({
                        purchaseOrderNo: item.purchase_order_no,
                      })}
                      aria-label={`${ACTION_WORDING.open} ${item.purchase_order_no}`}
                    >
                      {ACTION_WORDING.open}
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}
